import { useUi } from '../store';
import type { GameHandle } from '../game/useGame';

/**
 * Pause is a menu, not just a freeze. If a wave has gone badly the useful
 * question is "how far back do I want to go?", and there are three honest
 * answers: this wave, the whole level, or out.
 *
 * Restarting the wave rewinds to the restore point taken when its build phase
 * opened — you keep what you'd built and spent up to that moment. Restarting
 * the level starts a fresh run of it.
 */
export default function PauseMenu({
  game,
  onRestartLevel,
  onExit,
}: {
  game: GameHandle | null;
  onRestartLevel: () => void;
  onExit: () => void;
}): JSX.Element | null {
  const paused = useUi((s) => s.paused);
  if (!paused) return null;

  const resume = (): void => useUi.getState().setPaused(false);

  return (
    <div className="scrim" onPointerDown={resume}>
      <div className="dialog dialog--menu" role="dialog" onPointerDown={(e) => e.stopPropagation()}>
        <button className="menu-row is-primary" onClick={resume}>
          <Glyph d="M8 5 L26 16 L8 27 Z" />
          Resume
        </button>
        <button className="menu-row" onClick={() => game?.restartWave()}>
          <Glyph d="M9 7 A11 11 0 1 1 6 17" stroke />
          Restart wave
        </button>
        <button className="menu-row" onClick={onRestartLevel}>
          <Glyph d="M6 6 H10 V26 H6 Z M13 16 L26 6 V26 Z" />
          Restart level
        </button>
        <button className="menu-row" onClick={onExit}>
          <Glyph d="M18 6 H26 V26 H18 M14 11 L19 16 L14 21 M19 16 H5" stroke />
          Quit to map
        </button>
      </div>
    </div>
  );
}

function Glyph({ d, stroke }: { d: string; stroke?: boolean }): JSX.Element {
  return (
    <svg viewBox="0 0 32 32" className="icon icon--sm" aria-hidden="true">
      <path
        d={d}
        fill={stroke ? 'none' : 'currentColor'}
        stroke={stroke ? 'currentColor' : 'none'}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
